import _ from 'lodash';
import { observable, computed, action, toJS, makeObservable } from 'mobx';
import moment from 'moment';
import {canDeleteModel, canPatchModel} from './store';

class Enum {
  values;
  nullable;

  constructor(values, nullable) {
    this.values = values;
    this.nullable = nullable;
  }
}

export class DomainObject {
  links;

  constructor() {
    makeObservable(this, {
      links: observable,
      merge: action,
      update: action,
      canDelete: computed,
      canPatch: computed
    });
  }

  merge(partialModel) {
    if (!partialModel) return;

    _.merge(this, partialModel);

    const relations = this._relations || {};

    for (const key of ['createdAt', 'updatedAt', 'deletedAt']) {
      if (partialModel[key]) {
        this.hasDate(key, partialModel);
      }
    }

    _.forOwn(relations, (value, key) => {
      if (key === '_dates') {
        for (const dateProp of relations[key]) {
          this.hasDate(dateProp, partialModel);
        }
      } else if (key === '_numbers') {
        for (const numberProp of relations[key]) {
          this.hasNumber(numberProp, partialModel);
        }
      } else {
        if (_.isArray(value)) {
          this.hasMany(key, partialModel, value[0]);
        } else if (value instanceof Enum) {
          this.hasEnum(key, partialModel, value);
        } else {
          this.hasOne(key, partialModel, value);
        }
      }
    });

    this._afterMerge?.();
  }

  update(model) {
    _.forOwn(this, (value, key) => {
      if (_.startsWith(key, '_')) return;

      this[key] = undefined;
    });
    this.merge(model);
  }

  link(linkName) {
    const link = _.get(this.links, `${linkName}.href`);
    if (!link) {
      throw new Error(`There is no ${linkName} link on the object ${JSON.stringify(this)}.`);
    }
    return link;
  }

  hasLink(linkName) {
    return _.has(this.links, `${linkName}.href`);
  }

  equals(other) {
    return this.id === other.id;
  }

  pick(props = []) {
    return new this.constructor(_.pick(this, ['links', 'id', '_type', ...props]));
  }

  hasOne(name, other, _class) {
    if (other[name]) {
      this[name] = new _class(this[name]);
      this[name].merge(other[name]);
    }
  }

  hasMany(name, other, _class) {
    if (other[name]) {
      this[name] = this[name].map(t => new _class(t));
      for (const t of this[name]) {
        if (!t.id) continue;

        const existingItem = _.find(other[name], {id: t.id});
        t.merge(existingItem);
      }
    }
  }

  hasDate(name, other) {
    if (other[name]) {
      this[name] = new Date(moment(other[name]));
    }
  }

  hasNumber(name, other) {
    if (other[name]) {
      this[name] = Number(other[name]);
    }
  }

  hasEnum(name, other, value) {
    if ((other[name] || !value.nullable) && !_.includes(value.values, other[name])) {
      throw Error(`Expected one of the following: ${value.values}. Got instead: ${other[name]}. (${name} is an enum.)`);
    }

    if (other[name]) {
      this[name] = other[name];
    }
  }

  accessLevel(target) {
    if (!_.startsWith(target, '::')) {
      throw new Error(`Target name must be prefixed with two colons. Use model.hasAccess('::${target}').`);
    }

    const dataPermission = _.find(_.get(this, 'meta.dataPermissions'), {name: _.trimStart(target, '::')}) || {};

    switch (dataPermission.accessLevel) {
      case 'view':
        return 'r';
      case 'edit':
        return 'w';
      default:
        return false;
    }
  }

  get canDelete() {
    return canDeleteModel(this);
  }

  get canPatch() {
    return canPatchModel(this);
  }

  get isNew() {
    const id = _.get(this, 'id');
    if (!id) {
      return true;
    }

    return id.indexOf('-') !== -1;
  }

  toJS() {
    return toJS(this);
  }
}

export function oneOf(values, nullable = true) {
  return new Enum(values, nullable);
}
